import { createTheme } from "@mui/material/styles";

/** Shared layout tokens for Asset Owner, Investor, and Admin dashboards */
export const DRAWER_WIDTH = 260;
/** Admin sidebar rail width when collapsed (icons only) */
export const DRAWER_WIDTH_COLLAPSED = 72;

export const SIDEBAR_TRANSITION_MS = 220;

/** Fixed admin `Topbar` row height; keep `Dashboard` spacer and `AdminSidebar` header aligned. */
export const ADMIN_HEADER_HEIGHT_PX = 64;

/**
 * Single admin accent (selected nav pill, key links/icons). Matches `palette.primary.main`.
 */
export const ADMIN_SHELL_ACCENT = "#2563eb";
export const ADMIN_SHELL_ACCENT_HOVER = "#1d4ed8";

/**
 * Layout-only tokens (sidebar, drawer). All color/surface tokens live inside
 * the MUI theme palette below so components access them via theme.palette.*.
 */
export const dashboardShell = {
  drawerWidth: DRAWER_WIDTH,
  drawerWidthCollapsed: DRAWER_WIDTH_COLLAPSED,
  sidebar: {
    bg: "#0f172a",
    bgHeader: "#0c1222",
    border: "1px solid rgba(148, 163, 184, 0.12)",
    text: "#f1f5f9",
    textMuted: "#94a3b8",
    itemHover: "rgba(30, 41, 59, 0.95)",
    /* Active nav: blue rounded rectangle (same as asset owner / investor sidebars; not a pill). */
    itemActive: ADMIN_SHELL_ACCENT,
    itemActiveHover: ADMIN_SHELL_ACCENT_HOVER,
    itemActiveText: "#ffffff",
    itemActiveShadow: "none",
    itemActiveBorder: "none",
    iconMuted: "#94a3b8",
    iconActive: "#ffffff",
    logoMarkBg: "rgba(37, 99, 235, 0.2)",
    logoMarkBorder: "rgba(96, 165, 250, 0.35)",
    navItemRadiusExpanded: 1,
    navItemRadiusCollapsed: 12,
    navItemMinHeight: 44,
    dividerMuted: "rgba(148, 163, 184, 0.15)",
  },
  /** Shared admin UI (import in `components/admin/*` for one accent). */
  admin: {
    accent: ADMIN_SHELL_ACCENT,
    accentHover: ADMIN_SHELL_ACCENT_HOVER,
    accentSoftHoverBg: "rgba(37, 99, 235, 0.06)",
    pageBg: "#f8f9fa",
    platformChipRegulatedBg: "rgba(34, 197, 94, 0.14)",
    platformChipRegulatedColor: "#4ade80",
    platformChipVerifiedBg: "rgba(59, 130, 246, 0.14)",
    platformChipVerifiedColor: "#93c5fd",
  },
  surface: {
    app: "#f1f5f9",
    paper: "#ffffff",
    search: "#f1f5f9",
  },
  header: {
    border: "1px solid #e2e8f0",
  },
};

export function createDashboardTheme() {
  return createTheme({
    palette: {
      background: {
        default: "#f1f5f9",
        paper: "#ffffff",
        subtle: "#f9fafb",
        headerIcon: "#F9FAFB",
        activityIcon: "#f4f3ff",
        search: "#f1f5f9",
        /** Segmented tab track (investor loans tabs, etc.) */
        tabTrack: "#f1f3f5",
      },
      primary: { main: "#2563eb", dark: "#1d4ed8" },
      success: { main: "#16a34a" },
      text: {
        primary: "#111827",
        strong: "#020617",
        slate: "#0F172A",
        neutral: "#374151",
        secondary: "#64748B",
        muted: "#6b7280",
        faint: "#9ca3af",
        arrow: "#d1d5db",
        icon: "#5B5B5B",
      },
      divider: "#e5e7eb",
      border: {
        light: "#e5e7eb",
        medium: "#d1d5db",
        subtle: "#eef0f5",
      },
      brand: {
        cyan: "#00C4CC",
        cyanDark: "#009CA3",
        cyanSoft: "rgba(0, 196, 204, 0.08)",
        blue: "#2563eb",
        blueDark: "#1d4ed8",
        ctaGradient: "linear-gradient(90deg,#2563EB 0%,#06B6D4 100%)",
        ctaGradientHover: "linear-gradient(90deg,#1D4ED8 0%,#0891B2 100%)",
        ctaShadow: "0px 10px 30px rgba(37,99,235,0.35)",
        ctaShadowHover: "0px 10px 30px rgba(37,99,235,0.45)",
      },
      accent: {
        warning: {
          bg: "#fff7ed",
          border: "#ffedd5",
          iconBg: "#ffedd5",
          icon: "#c2410c",
          text: "#9a3412",
          actionBg: "#ea580c",
          actionBgHover: "#c2410c",
          /* Pending / "in review" status */
          pendingBg: "#fff3cd",
          pendingText: "#a66a00",
          /* Subtle yellow card background (e.g. KYC card) */
          cardBg: "#fff8e6",
          cardText: "#b26d00",
          /* Regulatory chip */
          chip: "#ffc857",
        },
        info: {
          border: "#bae6fd",
          icon: "#0369a1",
          text: "#0c4a6e",
        },
        teal: {
          chipBg: "#e0f2f1",
          chipText: "#0d9488",
          border: "#a5f3fc",
          text: "#155e75",
          dashedBorder: "#67e8f9",
          /* CTA / export button (TEAL) */
          action: "#00bfa5",
          actionHover: "#00a68c",
        },
        /** Light cyan surface used by investor asset stat boxes */
        cyan: {
          bgLight: "#ecfeff",
          bg: "#cffafe",
          text: "#0891b2",
          textDeep: "#155e75",
          /* Page-header icon tile (cyan → blue wash) */
          border: "#a2f4fd",
          tile: "linear-gradient(135deg, #ecfeff 0%, #eff6ff 100%)",
          /* Deep cyan used for record links (loan IDs, etc.) */
          deep: "#0092b8",
        },
        /** Informational "action required" banner (blue-50 wash, blue rule) */
        blue: {
          bg: "#eff6ff",
          border: "#2b7fff",
          title: "#1c398e",
          text: "#193cb8",
          action: "#155dfc",
          actionHover: "#1447e6",
          /* Soft blue outlines for the signature cards */
          borderSoft: "#bfdbfe",
          borderFaint: "#dbeafe",
          /* Solid blue for signing-flow controls and step progress */
          solid: "#2b7fff",
        },
        /** Verified / approved status colors (KYC, Nafath, etc.) */
        success: {
          bg: "#e6f7f0",
          text: "#1b9f5a",
          border: "#b2f0d2",
          soft: "#e9fff4",
          softer: "#f5fffb",
          chip: "#00c26f",
        },
        /** Dark navy used for the wallet card / regulatory banner. */
        navy: {
          primary: "#071b5f",
          dark: "#06124c",
          darkest: "#060e3d",
          darkestVar: "#050c38",
          accent: "#00a3ff",
          accentSoft: "#f2fbff",
          chip: "#2f80ff",
          button: ADMIN_SHELL_ACCENT,
        },
      },
      /** Accent colors for stat-card icons / subtitles. Named by hue, not by usage. */
      stats: {
        green: "#16a34a",
        emerald: "#10b981",
        blue: "#2563eb",
        sky: "#0ea5e9",
        indigo: "#6366f1",
        orange: "#ea580c",
        amber: "#f97316",
        amberWarn: "#f59e0b",
        purple: "#9333ea",
      },
      /** Status chip color tokens (admin tables, pending reviews, etc.) */
      chips: {
        warning: { bg: "#fff7e6", text: "#f57c00" },
        error: { bg: "#ffebee", text: "#c62828" },
        success: { bg: "#e8f5e9", text: "#2e7d32" },
        successDeep: { bg: "#e8f5e9", text: "#1b5e20" },
        pending: { bg: "#fff3e0", text: "#ef6c00" },
        info: { bg: "#e3f2fd", text: "#1976d2" },
        infoDeep: { bg: "#e3f2fd", text: "#1565c0" },
        neutral: { bg: "#f5f5f5", text: "#616161" },
        suspended: { bg: "#fdecea", text: "#c62828" },
        violet: { bg: "#f3e5f5", text: "#7b1fa2" },
        teal: { bg: "#e0f2f1", text: "#00695c" },
        slate: { bg: "#eceff1", text: "#455a64" },
      },
      /**
       * Outlined status badges (tinted fill + matching 1px border). Used where a
       * status needs more definition than a flat `chips.*` pill, e.g. loan tables.
       */
      badges: {
        info: { bg: "#dbeafe", border: "#8ec5ff", text: "#1447e6" },
        warning: { bg: "#fef3c6", border: "#ffd230", text: "#bb4d00" },
        success: { bg: "#dcfce7", border: "#7bf1a8", text: "#008236" },
        cyan: { bg: "#cefafe", border: "#53eafd", text: "#007595" },
      },
      /** Credit-score verification panel (SIMAH) — green wash + score scale. */
      credit: {
        bg: "linear-gradient(90deg, #f0fdf4 0%, #ecfdf5 100%)",
        border: "#b9f8cf",
        cardBorder: "#7bf1a8",
        score: "#00a63e",
        barFrom: "#00c950",
        barTo: "#00a63e",
        logo: "linear-gradient(135deg, #009966 0%, #00a63e 100%)",
      },
      /** Amber "next steps" / advisory note panel. */
      note: {
        bg: "#fffbeb",
        border: "#fee685",
        icon: "#e17100",
      },
      /** Table styling tokens used across admin list pages. */
      table: {
        headBg: "#f5f6fa",
        headText: "#444",
        rowHover: "#fafbfc",
      },
      /** Stat-card icon palette for the admin Platform Overview cards. */
      statIcon: {
        users: { bg: "#d1fae5", color: "#10b981" },
        assets: { bg: "#ede9fe", color: "#8b5cf6" },
        volume: { bg: "#dcfce7", color: "#22c55e" },
        revenue: { bg: "#dbeafe", color: "#3b82f6" },
      },
      shadow: {
        card: "0px 16px 40px rgba(15,23,42,0.06)",
        cardStrong: "0px 24px 60px rgba(15,23,42,0.12)",
        cardHover: "0px 8px 24px rgba(15,23,42,0.06)",
        statCard: "0px 4px 20px rgba(0,0,0,0.04)",
      },
      /** Gradient backgrounds for primary action buttons. */
      gradients: {
        teal: "linear-gradient(90deg, #00bfa5 0%, #06b6d4 100%)",
        tealHover: "linear-gradient(90deg, #00a68c 0%, #0891b2 100%)",
        /** Cyan → blue primary CTA (investor loans header) */
        cyanBlue: "linear-gradient(90deg, #0092b8 0%, #155dfc 100%)",
        cyanBlueHover: "linear-gradient(90deg, #007a99 0%, #1447e6 100%)",
        /** Blue → cyan, the mirrored variant (DocuSign CTA) */
        blueCyan: "linear-gradient(90deg, #155dfc 0%, #0092b8 100%)",
        blueCyanHover: "linear-gradient(90deg, #1447e6 0%, #007a99 100%)",
        purple: "linear-gradient(90deg, #a855f7 0%, #7c3aed 100%)",
        purpleHover: "linear-gradient(90deg, #9333ea 0%, #6d28d9 100%)",
      },
    },
    typography: {
      fontFamily:
        "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      h6: { fontWeight: 700 },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { textTransform: "none", fontWeight: 600 },
        },
        variants: [
          {
            props: { variant: "cyanText" },
            style: ({ theme }) => ({
              color: theme.palette.brand.cyan,
              backgroundColor: "transparent",
              padding: "4px 8px",
              minWidth: 0,
              "&:hover": {
                backgroundColor: theme.palette.brand.cyanSoft,
              },
            }),
          },
        ],
      },
    },
  });
}
